/**
 * Completion Notification Sender
 * Sends notifications to staff when an artist completes their profile or show details
 */

import { getCompletionNotificationRoleNames } from './completion-notification-roles.js';
import { getPeopleParticipantsByRoles } from './people-participants.js';
import { sendCompletionNotificationEmail, sendTechnicalUpdateNotificationEmail, getEmailBrandingFromAccount, isEmailServiceConfigured } from './email.js';
import { getClient } from './client.js';
import { TABLES } from './tables.js';
import { logger } from './logger.js';
import type { CompletionNotificationEntityType } from './types.js';

interface NotificationContext {
  entityType: CompletionNotificationEntityType;
  entityId: string;
  entityName: string;
  completedByName: string;
  completedAt: string;
  accountId: string;
  baseUrl?: string;
}

/**
 * Generate a link to view an entity in the admin interface
 */
function generateViewLink(entityType: CompletionNotificationEntityType, entityId: string, baseUrl?: string): string {
  const base = baseUrl || process.env.ADMIN_BASE_URL || 'http://localhost:5173';
  const page = entityType === 'person' ? 'people' : 'happenings';
  return `${base}/${page}?openEntityId=${entityId}&entityType=${entityType}`;
}

/**
 * Get account information for branding and organization name
 */
async function getAccountInfo(accountId: string): Promise<{ name: string; branding?: any; timezone?: string } | null> {
  try {
    const { data, error } = await getClient()
      .from(TABLES.accounts)
      .select('name, settings, timezone')
      .eq('id', accountId)
      .single();

    if (error || !data) return null;

    return {
      name: data.name,
      branding: getEmailBrandingFromAccount({ settings: data.settings }),
      timezone: data.timezone,
    };
  } catch {
    return null;
  }
}

/**
 * Get happening participants by roles
 */
async function getHappeningParticipantsByRoles(
  happeningId: string,
  roles: string[]
): Promise<Array<{ user_id: string | null; user_email: string; user_name: string }>> {
  if (roles.length === 0) return [];

  const { data, error } = await getClient()
    .from(TABLES.happening_participants)
    .select(`
      user_id,
      users:user_id (email, name)
    `)
    .eq('happening_id', happeningId)
    .in('role', roles)
    .not('user_id', 'is', null);

  if (error) {
    logger.error('happening_participants_fetch_failed', { happeningId, error: error.message });
    return [];
  }

  // Filter and map to get unique users with email
  const userMap = new Map<string, { user_id: string; user_email: string; user_name: string }>();

  for (const row of data || []) {
    const userData = row.users as any;
    if (row.user_id && userData?.email) {
      userMap.set(row.user_id, {
        user_id: row.user_id,
        user_email: userData.email,
        user_name: userData.name || 'Staff Member',
      });
    }
  }

  return Array.from(userMap.values());
}

/**
 * Send completion notifications to staff assigned to configured roles
 */
export async function sendCompletionNotifications(context: NotificationContext): Promise<void> {
  if (!isEmailServiceConfigured()) {
    logger.info('completion_notification_skipped', { reason: 'email_service_not_configured', entityType: context.entityType, entityId: context.entityId });
    return;
  }

  try {
    // 1. Get notification roles for this entity
    const notifyRoles = await getCompletionNotificationRoleNames(context.entityType, context.entityId);

    if (notifyRoles.length === 0) {
      logger.info('completion_notification_skipped', { reason: 'no_roles_configured', entityType: context.entityType, entityId: context.entityId });
      return;
    }

    logger.debug('completion_notification_roles_found', { entityType: context.entityType, entityId: context.entityId, roles: notifyRoles });

    // 2. Get participants assigned to those roles
    let recipients: Array<{ email: string; name: string }> = [];

    if (context.entityType === 'person') {
      const participants = await getPeopleParticipantsByRoles(context.entityId, notifyRoles);

      recipients = participants
        .filter(p => p.user_id && p.user_email)
        .map(p => ({
          email: p.user_email!,
          name: p.user_name || 'Staff Member',
        }));
    } else {
      const participants = await getHappeningParticipantsByRoles(context.entityId, notifyRoles);

      recipients = participants.map(p => ({
        email: p.user_email,
        name: p.user_name,
      }));
    }

    // Deduplicate by email
    const uniqueRecipients = Array.from(
      new Map(recipients.map(r => [r.email.toLowerCase(), r])).values()
    );

    if (uniqueRecipients.length === 0) {
      logger.info('completion_notification_skipped', { reason: 'no_recipients', entityType: context.entityType, entityId: context.entityId, roles: notifyRoles });
      return;
    }

    // 3. Get account info for branding
    const accountInfo = await getAccountInfo(context.accountId);
    const viewLink = generateViewLink(context.entityType, context.entityId, context.baseUrl);

    // 4. Send notifications to each recipient
    const results = await Promise.allSettled(
      uniqueRecipients.map(async (recipient) => {
        await sendCompletionNotificationEmail({
          toEmail: recipient.email,
          toName: recipient.name,
          entityType: context.entityType,
          entityName: context.entityName,
          completedByName: context.completedByName,
          completedAt: context.completedAt,
          viewLink,
          organizationName: accountInfo?.name,
          branding: accountInfo?.branding,
          timezone: accountInfo?.timezone,
        });
        return recipient.email;
      })
    );

    const successes = results.filter(r => r.status === 'fulfilled');
    const failures = results.filter(r => r.status === 'rejected');

    if (failures.length > 0) {
      failures.forEach((failure, index) => {
        const email = uniqueRecipients[index]?.email || 'unknown';
        const error = failure.status === 'rejected' ? failure.reason : 'Unknown error';
        logger.error('completion_notification_send_failed', {
          recipientEmail: email,
          entityType: context.entityType,
          entityId: context.entityId,
          error: error instanceof Error ? error.message : String(error),
        });
      });
    }

    logger.info('completion_notification_batch_complete', {
      entityType: context.entityType,
      entityId: context.entityId,
      sent: successes.length,
      failed: failures.length,
      total: uniqueRecipients.length,
    });

    // If ALL emails failed, throw an error so the notification can be retried
    if (failures.length > 0 && successes.length === 0) {
      const failureMessages = failures.map((f, i) => {
        const email = uniqueRecipients[i]?.email || 'unknown';
        const errorMsg = f.status === 'rejected' && f.reason instanceof Error ? f.reason.message : String(f.reason);
        return `${email}: ${errorMsg}`;
      }).join('; ');
      throw new Error(`All email notifications failed: ${failureMessages}`);
    }

    if (failures.length > 0) {
      logger.warn('completion_notification_partial_failure', {
        entityType: context.entityType,
        entityId: context.entityId,
        failed: failures.length,
        succeeded: successes.length,
        total: uniqueRecipients.length,
      });
    }
  } catch (error) {
    logger.error('completion_notification_error', {
      entityType: context.entityType,
      entityId: context.entityId,
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
}

/**
 * Send completion notifications for a person (profile completion)
 */
export async function sendPersonCompletionNotifications(
  personId: string,
  personName: string,
  accountId: string,
  completedAt?: string
): Promise<void> {
  await sendCompletionNotifications({
    entityType: 'person',
    entityId: personId,
    entityName: personName,
    completedByName: personName, // The artist completed their own profile
    completedAt: completedAt || new Date().toISOString(),
    accountId,
  });
}

/**
 * Send technical update notifications to engineers assigned to a happening
 * Triggered when an artist updates equipment requests or technical notes
 */
export async function sendTechnicalUpdateNotifications(context: {
  happeningId: string;
  happeningTitle: string;
  updatedByName: string;
  changeDescription: string;
  accountId: string;
  baseUrl?: string;
}): Promise<void> {
  if (!isEmailServiceConfigured()) {
    logger.info('technical_update_notification_skipped', { reason: 'email_service_not_configured', happeningId: context.happeningId });
    return;
  }

  try {
    // Get engineers assigned to this happening
    const engineers = await getHappeningParticipantsByRoles(context.happeningId, ['engineer']);

    if (engineers.length === 0) {
      logger.info('technical_update_notification_skipped', { reason: 'no_engineers_assigned', happeningId: context.happeningId });
      return;
    }

    const accountInfo = await getAccountInfo(context.accountId);
    const viewLink = generateViewLink('happening', context.happeningId, context.baseUrl);
    const updatedAt = new Date().toISOString();

    const results = await Promise.allSettled(
      engineers.map(async (engineer) => {
        await sendTechnicalUpdateNotificationEmail({
          toEmail: engineer.user_email,
          toName: engineer.user_name,
          happeningTitle: context.happeningTitle,
          updatedByName: context.updatedByName,
          updatedAt,
          changeDescription: context.changeDescription,
          viewLink,
          organizationName: accountInfo?.name,
          branding: accountInfo?.branding,
          timezone: accountInfo?.timezone,
        });
        return engineer.user_email;
      })
    );

    const successes = results.filter(r => r.status === 'fulfilled');
    const failures = results.filter(r => r.status === 'rejected');

    if (failures.length > 0) {
      failures.forEach((failure, index) => {
        const email = engineers[index]?.user_email || 'unknown';
        const error = failure.status === 'rejected' ? failure.reason : 'Unknown error';
        logger.error('technical_update_notification_send_failed', {
          recipientEmail: email,
          happeningId: context.happeningId,
          error: error instanceof Error ? error.message : String(error),
        });
      });
    }

    logger.info('technical_update_notifications_complete', {
      happeningId: context.happeningId,
      sent: successes.length,
      failed: failures.length,
      total: engineers.length,
    });
  } catch (error) {
    logger.error('technical_update_notification_error', {
      happeningId: context.happeningId,
      error: error instanceof Error ? error.message : String(error),
    });
    // Don't throw - technical update notifications should not block the save
  }
}

/**
 * Send completion notifications for a happening (show details completion)
 */
export async function sendHappeningCompletionNotifications(
  happeningId: string,
  happeningTitle: string,
  accountId: string,
  completedByName: string,
  completedAt?: string
): Promise<void> {
  await sendCompletionNotifications({
    entityType: 'happening',
    entityId: happeningId,
    entityName: happeningTitle,
    completedByName,
    completedAt: completedAt || new Date().toISOString(),
    accountId,
  });
}
